<template>
  <div :id="id" ref="jtTableRef" :key="key">
    <template v-if="showAffixTabel">
         <!-- 吸顶占位：vue-affix 固定态用 position:fixed 会脱离文档流，页面高度瞬间少一块；
              数据量刚好出滚动条时会反复「固定→滚动条消失→解除→再固定」。host 在固定态补回同高度，断掉循环 -->
         <div ref="affixHostRef" class="jt-table-affix-host" :style="affixHostStyle">
         <affix
            ref="affixRef"
            @affix="handleAffixState('affix')"
            @affixtop="handleAffixState('affixtop')"
            @affixbottom="handleAffixState('affixbottom')"
            :relative-element-selector="relativeElementSelector"
            :style="{ width: wrapperWidth + 'px', zIndex: zIndex }"
            :scrollContainerSelector="scrollContainerSelector"
            :offset="offset"
            :enabled="enabled"
         >
            <!-- workOrder_v5 §11：表头之前的「随表头一起吸顶」插槽（如排单管理批量操作区）。白底，固定态下不露出表体行；不传则为空、零影响 -->
            <div class="jt-table-affix-prepend" v-if="$slots['affix-prepend']"><slot name="affix-prepend"/></div>
            <TableHeader
                :header-cell-style="{ background: '#e8eaec', color: '#333' }"
                v-bind="$attrs"
                v-on="$listeners"
                @selection-change="handleSelectionChange"
                class="jt-table-header"
                height="auto"
                ref="tableHeaderRef"
                @header-dragend="headerDragend"
                :comWidth="width"
            >
                <template v-for="(item,keys) in passSlots()" v-slot:[keys]>
                    <slot :name="keys" />
                </template>
            </TableHeader>
        </affix>
        </div>
        <TableBody
            v-bind="$attrs"
            v-on="$listeners"
            class="jt-table-body"
            @selection-change="handleSelectionChange"
            ref="tableBodyRef"
            :show-header="false"
            :width.sync="width"
        >
            <template v-for="(item,keys) in passSlots()" v-slot:[keys]>
                <slot :name="keys" />
            </template>
        </TableBody>
    </template>
    <template v-else-if="showWrapper">
         <!-- enabled=false 回退单表模式：插槽内容原位渲染在表格上方 -->
         <div class="jt-table-affix-prepend" v-if="$slots['affix-prepend']"><slot name="affix-prepend"/></div>
         <el-table
            :header-cell-style="{ background: '#e8eaec', color: '#333' }"
            v-bind="$attrs"
            v-on="$listeners"
            ref="defaultTableRef"
        >
            <template v-for="(item,keys) in passSlots()" v-slot:[keys]>
                <slot :name="keys" />
            </template>
        </el-table>
    </template>
  </div>
</template>

<script>
import { Affix } from 'vue-affix';
import TableHeader from './TableHeader.vue'
import TableBody from './TableBody.vue'

function toggleRowStatus(statusArr, row, newVal) {
  let changed = false;
  const index = statusArr.indexOf(row);
  const included = index !== -1;

  const addRow = () => {
    statusArr.push(row);
    changed = true;
  };
  const removeRow = () => {
    statusArr.splice(index, 1);
    changed = true;
  };

  if (typeof newVal === 'boolean') {
    if (newVal && !included) {
      addRow();
    } else if (!newVal && included) {
      removeRow();
    }
  } else {
    if (included) {
      removeRow();
    } else {
      addRow();
    }
  }
  return changed;
}
export default {
    inheritAttrs: false,
    name: "JtTable",
    components: {
        Affix,
        TableHeader,
        TableBody
    },
    provide() {
        return {
            tableGlobal: this,
        }
    },
    props: {
        affixConfig: {
             type: Object,
             default: ()=> {
                return {}
             }
        },
    },
    data() {
        return {
            wrapperWidth: 0,
            showWrapper: false,
            hasResizeObserver: false,
            id: `affix_id_${Date.now()}`,
            scrollContainerSelector: null,
            key: 0,
            bodyWrapperData: null,
            width: null,
            // 吸顶态占位高度：固定态 = 表头块实际高度，非固定态 = 0
            affixPlaceholderHeight: 0,
            affixClassObserver: null,
            affixResizeObserver: null,
        };
    },
    computed: {
        showAffixTabel() {
            return this.showWrapper && this.hasResizeObserver && this.enabled
        },
        affixHostStyle() {
            return this.affixPlaceholderHeight
                ? { height: this.affixPlaceholderHeight + 'px' }
                : {}
        },
        offset() {
            return this.affixConfig.offset ?? { top: 50, bottom: 0 }
        },
        zIndex() {
            return this.affixConfig.zIndex ?? 9
        },
        enabled() {
            return this.affixConfig.enabled ?? true
        },
        relativeElementSelector() {
            return `#${this.id}`
        }
    },
    created() {
        // 兼容处理
        this.hasResizeObserver = 'ResizeObserver' in window;
    },
    mounted() {
        this.$nextTick(() => {
            const scrollBox = this.getParentScrollable(this.relativeElementSelector);
            if (scrollBox) {
                const elementId = `affix_scroll_id_${Date.now()}`
                scrollBox.id = elementId;
                this.scrollContainerSelector = `#${elementId}`;
            }
            this.getWrapperWidth();
            // 等 affix 渲染完成后再挂占位观察器（showWrapper 置 true 后 refs 才出现）
            this.$nextTick(() => this.initAffixObservers());
            if (!this.hasResizeObserver) {
                return
            }
            var ro = new ResizeObserver( entries => {
                this.getWrapperWidth();
                this.doLayout();
            });
            // 观察一个或多个元素
            ro.observe(this.$refs.jtTableRef);
            this.$once('hook:beforeDestroy', () => {
                ro.unobserve(this.$refs.jtTableRef)
            });
        });
    },
    methods: {
        /**
         * 吸顶占位观察：vue-affix 固定态是 position:fixed，会脱离文档流导致页面高度骤减。
         * 数据量刚好出滚动条时会形成「固定→滚动条消失→解除→再固定」死循环（表头抖动/滚不动）。
         * 这里在固定态把 host 撑到表头块实际高度，非固定态清零；高度变化（批量区换行/勾选变化）用 ResizeObserver 同步。
         */
        initAffixObservers() {
            this.destroyAffixObservers()
            const affixEl = this.$refs.affixRef && this.$refs.affixRef.$el
            if (!affixEl) {
                return
            }
            this.syncAffixPlaceholder()
            if (window.MutationObserver) {
                this.affixClassObserver = new MutationObserver(() => this.syncAffixPlaceholder())
                this.affixClassObserver.observe(affixEl, { attributes: true, attributeFilter: ['class'] })
            }
            if (window.ResizeObserver) {
                this.affixResizeObserver = new ResizeObserver(() => {
                    if (affixEl.classList.contains('affix')) {
                        this.syncAffixPlaceholder()
                    }
                })
                this.affixResizeObserver.observe(affixEl)
            }
        },
        syncAffixPlaceholder() {
            const affixEl = this.$refs.affixRef && this.$refs.affixRef.$el
            if (!affixEl) {
                return
            }
            this.applyAffixHostHeight(affixEl.classList.contains('affix') ? affixEl.offsetHeight : 0)
        },
        /**
         * vue-affix 切换状态时同步设置占位高度。事件在 class 切换前触发，
         * 这里直接改 DOM，避免等 Vue 下一帧渲染：固定态脱离文档流的那一帧
         * 页面高度短暂变矮，浏览器会把 scrollTop 夹到新的最大值（接近底部时表现为「自己往上滚一段」）。
         */
        handleAffixState(state) {
            const affixEl = this.$refs.affixRef && this.$refs.affixRef.$el
            if (!affixEl) {
                return
            }
            this.applyAffixHostHeight(state === 'affix' ? affixEl.offsetHeight : 0)
        },
        applyAffixHostHeight(h) {
            const host = this.$refs.affixHostRef
            if (host) {
                host.style.height = h > 0 ? h + 'px' : ''
            }
            if (h !== this.affixPlaceholderHeight) {
                this.affixPlaceholderHeight = h
            }
        },
        destroyAffixObservers() {
            if (this.affixClassObserver) {
                this.affixClassObserver.disconnect()
                this.affixClassObserver = null
            }
            if (this.affixResizeObserver) {
                this.affixResizeObserver.disconnect()
                this.affixResizeObserver = null
            }
        },
        /** 透传给表头/表体的具名插槽：剔掉本组件自用的 affix-prepend（§11），其余原样 */
        passSlots() {
            const out = {};
            Object.keys(this.$slots || {}).forEach(k => { if (k !== 'affix-prepend') out[k] = this.$slots[k]; });
            return out;
        },
        headerDragend() {
            const hColumns = this.$refs.tableHeaderRef.columns;
            const tableBodyRef = this.$refs.tableBodyRef;
            const bColumns = tableBodyRef.columns;
            const list = bColumns.filter((item, index) => {
                return item.width !== hColumns[index].width;
            });
            if (list.length) {
                list.forEach(item => {
                    const index = bColumns.findIndex(i => i.id === item.id);
                    console.log( item.label, item.width, hColumns[index].width, hColumns[index].label);
                    item.width = hColumns[index].width;
                });
                tableBodyRef.store.scheduleLayout();
            };
        },
        getParentScrollable(element) {
            element =  document.querySelector(element);
           const overflowScrollReg = /scroll|auto|overlay/i;
            let parent = element.parentElement;
            while (parent) {
                const { overflowY } = window.getComputedStyle(parent);
                if (overflowScrollReg.test(overflowY)) {
                    return parent;
                }
                parent = parent.parentElement;
            }
            return null; // 如果没有找到，返回null
        },
        sort(...args) {
            this.runTwotableFns('sort', ...args)
        },
        doLayout(...args) {
            this.runTwotableFns('doLayout', ...args)
        },
        clearFilter(...args) {
            this.runTwotableFns('clearFilter', ...args)
        },
        clearSort(...args) {
            this.runTwotableFns('clearSort', ...args)
        },
        toggleRowExpansion(...args) {
            this.runTwotableFns('setCurrentRow', ...args)
        },
        toggleRowExpansion(...args) {
            this.runTwotableFns('toggleRowExpansion', ...args)
        },
        toggleAllSelection(...args) {
            this.runTwotableFns('toggleAllSelection', ...args)
        },
        toggleRowSelection(...args) {
            this.runTwotableFns('toggleRowSelection', ...args)
        },
        clearSelection() {
            this.runTwotableFns('clearSelection')
        },
        runTwotableFns(fnkey, ...args) {
            if (this.showAffixTabel) {
                if (['toggleRowExpansion', 'setCurrentRow'].includes(fnkey)) {
                    this.$refs.tableBodyRef[fnkey](...args);
                }else {
                    this.$refs.tableHeaderRef[fnkey](...args)
                    this.$refs.tableBodyRef[fnkey](...args);
                }
            }else {
                this.$refs.defaultTableRef[fnkey](...args)
            }
        },
        // 只能调用 原生的方法更新状态  否则会触发 change事件重复触发
        handleSelectionChange(selection = []) {
            const HStates = this.$refs.tableHeaderRef.store.states;
            const Hlist = HStates.data;
            HStates.isAllSelected = Hlist.length == selection.length;
            HStates.selection = [];

            const BStates = this.$refs.tableBodyRef.store.states;
            const Blist = BStates.data;
            BStates.isAllSelected = Blist.length == selection.length;
            BStates.selection = [];

            selection.map(row=> {
                toggleRowStatus(HStates.selection, row, true)
                toggleRowStatus(BStates.selection, row, true)
            });
        },
        getWrapperWidth() {
            this.wrapperWidth = this.$refs.jtTableRef.clientWidth;
            this.showWrapper = true;
        },
    },
    beforeDestroy() {
        this.destroyAffixObservers()
    },
    watch: {
        enabled() {
            this.destroyAffixObservers()
            this.affixPlaceholderHeight = 0
            this.key ++;
            this.$nextTick(() => this.initAffixObservers())
        },
        showAffixTabel() {
            this.$nextTick(() => this.initAffixObservers())
        }
    }
}
</script>

<style lang="scss" scoped>
.jt-table-affix-prepend { background: #fff; }
</style>